import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const AuthCallback = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Set language from URL parameter
    const lang = searchParams.get('lang');
    if (lang && ['en', 'fr', 'de', 'lv', 'lt'].includes(lang)) {
      i18n.changeLanguage(lang);
      localStorage.setItem('i18nextLng', lang);
    }

    // Process the callback
    processCallback();
  }, []);

  const processCallback = async () => {
    try {
      console.log('Processing callback...');
      console.log('Hash:', window.location.hash);
      console.log('Search:', window.location.search);

      // If there's a hash with access_token, Supabase should handle it
      // We need to give it time to process
      if (window.location.hash && window.location.hash.includes('access_token')) {
        console.log('Found access_token in hash, waiting for Supabase to process...');
        
        // Wait for Supabase to process the hash
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      // Now check for session
      const { data, error } = await supabase.auth.getSession();
      
      console.log('Session check result:', { data, error });

      if (error) {
        throw error;
      }

      if (data.session) {
        console.log('Session found!', data.session.user.email);
        await handleSuccess(data.session);
      } else {
        // Try one more time after a delay
        console.log('No session yet, retrying...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const { data: retryData, error: retryError } = await supabase.auth.getSession();
        
        if (retryError) {
          throw retryError;
        }
        
        if (retryData.session) {
          console.log('Session found on retry!');
          await handleSuccess(retryData.session);
        } else {
          throw new Error('Could not establish session');
        }
      }
    } catch (error) {
      console.error('Callback error:', error);
      setStatus('error');
      setErrorMessage(error.message || t('auth.errorCallback'));
    }
  };

  const handleSuccess = async (session) => {
    const user = session.user;
    const email = user.email?.toLowerCase();

    console.log('Handling success for:', email);

    // Try to save profile
    try {
      // Get pending signup data
      const { data: pendingData } = await supabase
        .from('pending_signups')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      const profileData = {
        id: user.id,
        email: email,
        first_name: pendingData?.first_name || user.user_metadata?.first_name || null,
        last_name: pendingData?.last_name || user.user_metadata?.last_name || null,
        organization: pendingData?.organization || user.user_metadata?.organization || null,
        role: pendingData?.requested_role || user.user_metadata?.requested_role || 'viewer',
        lang: pendingData?.lang || user.user_metadata?.lang || 'en',
        updated_at: new Date().toISOString()
      };

      await supabase.from('profiles').upsert(profileData, { onConflict: 'id' });
      console.log('Profile saved');

      // Set language
      if (profileData.lang && ['en', 'fr', 'de', 'lv', 'lt'].includes(profileData.lang)) {
        i18n.changeLanguage(profileData.lang);
        localStorage.setItem('i18nextLng', profileData.lang);
      }
    } catch (e) {
      console.log('Profile save error (non-blocking):', e);
    }

    setStatus('success');

    // Use window.location for a clean redirect
    setTimeout(() => {
      window.location.replace('/');
    }, 1000);
  };

  return (
    <div className="auth-callback-page">
      <div className="auth-callback-container">
        {status === 'processing' && (
          <div className="auth-callback-status">
            <Loader2 size={48} className="animate-spin callback-loader" />
            <h2>{t('auth.verifying')}</h2>
            <p>{t('auth.pleaseWait')}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="auth-callback-status success">
            <CheckCircle size={48} className="callback-success-icon" />
            <h2>{t('auth.loginSuccess')}</h2>
            <p>{t('auth.redirecting')}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="auth-callback-status error">
            <AlertCircle size={48} className="callback-error-icon" />
            <h2>{t('auth.loginError')}</h2>
            <p>{errorMessage}</p>
            <Button onClick={() => window.location.href = '/login'} className="callback-retry-btn">
              {t('auth.backToLogin')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
